import './TeamsViewDatatable.scss';

import { Button, Chip, Stack } from '@mui/material';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { TeamStatusEnum } from 'domain/team/TeamStatusEnum';
import { UseFetchWithFilterPageReturn } from 'primary/hooks/useFetchWithFilterPage';
import React from 'react';

import { PlayerTeam } from '../../../domain/event/PlayerTeam';
import { Team, TeamFilters } from '../../../domain/team/Team';
import { Translate } from '../../../domain/translation/Translation.repository';
import { TagPlayer } from '../../Components/TagPlayer/TagPlayer';
import { useContextDependency } from '../../hooks/useContextDependency';
import { useTranslate } from '../../hooks/useTranslate';

interface TeamsViewDatatableParametersProps {
  teams?: Team[];
  reFetch: () => void;
  eventId: number;
  disabled?: boolean;
  useFetchReturn: UseFetchWithFilterPageReturn<Team, TeamFilters>;
}

export const getTeamStatusObject = (team: Team, t: Translate) => {
  if (team.status === TeamStatusEnum.WAIT_VALIDATE) {
    return {
      label: t('event.teams.datatable.statusWaiting'),
      state: 'warning',
    };
  }
  if (team.status === TeamStatusEnum.WAIT_VALIDATE_PLAYERS) {
    return {
      label: t('event.teams.datatable.statusWaitingPlayer'),
      state: 'warning',
    };
  }
  if (team.status === TeamStatusEnum.REGISTERED) {
    return {
      label: t('event.teams.datatable.statusAccepted'),
      state: 'success',
    };
  }
  if (team.status === TeamStatusEnum.REJECTED) {
    return {
      label: t('event.teams.datatable.statusRefused'),
      state: 'error',
    };
  }
};

const formatTeamsToRowData = (teams: Team[], t: Translate) => {
  return teams.map((team) => ({
    id: team.id,
    name: team.name,
    players: team,
    statut: getTeamStatusObject(team, t),
    action: team,
  }));
};

export const TeamsViewDatataleParameters = ({
  teams,
  reFetch,
  eventId,
  disabled,
  useFetchReturn,
}: TeamsViewDatatableParametersProps) => {
  const { teamRepository } = useContextDependency();
  const t = useTranslate();

  const onClickDelete = (teamId: number) => () =>
    teamRepository.deleteTeamForEvent(teamId, eventId).then(() => reFetch());

  const onClickAccept = (teamId: number) => () =>
    teamRepository
      .responseParticipationEvent(true, teamId, eventId)
      .then(() => reFetch());

  const onClickDecline = (teamId: number) => () =>
    teamRepository.responseParticipationEvent(false, teamId, eventId);

  const columns: GridColDef[] = [
    {
      field: 'name',
      headerName: t('event.teams.datatable.labelName'),
      flex: 1,
      headerClassName: '-header',
      headerAlign: 'center',
      sortable: false,
    },
    {
      field: 'players',
      headerName: t('event.teams.datatable.labelPlayers'),
      flex: 2,
      headerAlign: 'center',
      sortable: false,
      headerClassName: '-header',
      renderCell: (value: GridRenderCellParams<Team>) => {
        if (!value.value) return;
        return (
          <div className={'-playersLink'}>
            {value.value.players.map((player: PlayerTeam) => (
              <TagPlayer key={player?.player?.idPlayer} player={player.player} />
            ))}
          </div>
        );
      },
    },
    {
      field: 'statut',
      headerName: t('event.teams.datatable.labelStatut'),
      flex: 1,
      headerClassName: '-header',
      headerAlign: 'center',
      sortable: false,
      renderCell: (
        value: GridRenderCellParams<{
          label: string;
          state: 'success' | 'error' | 'warning';
        }>,
      ) => (
        <Chip
          label={value.value?.label}
          variant="outlined"
          color={value.value ? value.value.state : 'warning'}
        />
      ),
    },
    {
      field: 'action',
      headerName: t('event.teams.datatable.labelActions'),
      headerClassName: '-header',
      headerAlign: 'center',
      sortable: false,
      flex: 2,
      renderCell: (value: GridRenderCellParams<Team>) => {
        if (!value.value) return;
        return (
          <div className={'-actions'}>
            {value.value.status === TeamStatusEnum.WAIT_VALIDATE && (
              <>
                <Button
                  variant={'contained'}
                  color={'success'}
                  onClick={onClickAccept(value.value.id)}
                  disabled={disabled}
                >
                  {t('event.teams.datatable.acceptAction')}
                </Button>
                <Button
                  variant={'contained'}
                  color={'error'}
                  onClick={onClickDecline(value.value.id)}
                  disabled={disabled}
                >
                  {t('event.teams.datatable.declineAction')}
                </Button>
              </>
            )}
            {value.value.status === TeamStatusEnum.REGISTERED && (
              <Button
                variant={'contained'}
                color={'error'}
                onClick={onClickDelete(value.value.id)}
                disabled={disabled}
              >
                {t('event.teams.datatable.deleteAction')}
              </Button>
            )}
            {value.value.status === TeamStatusEnum.REJECTED && (
              <span>{t('event.teams.datatable.noAction')}</span>
            )}
          </div>
        );
      },
    },
  ];

  return (
    <div className={'teamsViewDatatable'}>
      <DataGrid
        disableColumnFilter
        disableColumnMenu
        disableColumnSelector
        disableDensitySelector
        disableEval
        disableVirtualization
        pagination
        paginationMode={'server'}
        rowCount={useFetchReturn.fetchedResource?.totalElementsCount}
        paginationModel={{
          pageSize: useFetchReturn.fetchedResource?.pageSize || 0,
          page: useFetchReturn.fetchedResource?.currentPage || 0,
        }}
        onPaginationModelChange={({ pageSize, page }) => {
          if (pageSize !== useFetchReturn.fetchedResource?.pageSize) {
            useFetchReturn.changeNbPerPage(pageSize);
          }
          useFetchReturn.goToPage(page);
        }}
        loading={useFetchReturn.loading}
        className={'-parameters'}
        autoHeight
        sx={{ color: 'white' }}
        components={{
          //@ts-ignore
          noRowsOverlay: () => (
            <Stack height="100%" alignItems="center" justifyContent="center">
              {t('event.teams.datatable.noContentLabel')}
            </Stack>
          ),
        }}
        rows={teams ? formatTeamsToRowData(teams, t) : []}
        columns={columns}
      />
    </div>
  );
};
